import React, { useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { CmBackButton } from './CmBackButton';

const FIRST_MET_DATE = '2025-03-09';
const FIRST_DATE_DATE = '2025-12-07';
const DAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

function daysBetween(dateStr, now) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return Math.floor((today - d) / (1000 * 60 * 60 * 24));
}

function getWeekKey(dateStr) {
  const d = new Date(dateStr);
  const jan1 = new Date(d.getFullYear(), 0, 1);
  const dayOfYear = Math.floor((d - jan1) / (1000 * 60 * 60 * 24));
  const weekNum = Math.ceil((dayOfYear + jan1.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

function getMonthKey(dateStr) {
  return dateStr.slice(0, 7);
}

function daysInMonth(yearMonthKey) {
  const [y, m] = yearMonthKey.split('-').map(Number);
  return new Date(y, m, 0).getDate();
}

/* ── Styled Components ── */

const Container = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #0a0c10;
  z-index: 1500;
  color: #e9ecf1;
  overflow: hidden;
`;

const Header = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(7, 10, 18, 0.85);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 1rem;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px 16px 40px;
`;

const SectionTitle = styled.h3`
  margin: 28px 0 12px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(233, 236, 241, 0.6);
  &:first-child { margin-top: 0; }
`;

/* ── Number cards ── */
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const NumCard = styled.div`
  padding: 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const NumValue = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: #a8ccf0;
  line-height: 1.2;
`;

const NumLabel = styled.div`
  font-size: 0.8rem;
  margin-top: 4px;
  opacity: 0.65;
`;

/* ── Top spots ── */
const SpotRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  &:last-child { border-bottom: none; }
`;

const SpotRank = styled.span`
  width: 24px;
  text-align: center;
  font-weight: 700;
  font-size: 0.9rem;
  color: #a8ccf0;
`;

const SpotName = styled.span`
  flex: 1;
  font-size: 0.9rem;
`;

const SpotCount = styled.span`
  font-size: 0.85rem;
  opacity: 0.6;
`;

/* ── Bar chart ── */
const ToggleRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const ToggleBtn = styled.button`
  padding: 5px 14px;
  border-radius: 20px;
  border: 1.5px solid ${(p) => (p.$active ? 'rgba(91, 143, 214, 0.8)' : 'rgba(255, 255, 255, 0.2)')};
  background: ${(p) => (p.$active ? 'rgba(91, 143, 214, 0.25)' : 'transparent')};
  color: ${(p) => (p.$active ? '#a8ccf0' : '#c8ccd4')};
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const BarLabel = styled.span`
  width: 72px;
  flex-shrink: 0;
  font-size: 0.75rem;
  text-align: right;
  opacity: 0.7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 20px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 4px;
  overflow: hidden;
  ${(p) => p.$full && css`animation: ${barGlow} 2s ease-in-out infinite;`}
`;

const barGlow = keyframes`
  0%, 100% {
    box-shadow:
      0 0 6px 1px rgba(255, 120, 60, 0.5),
      0 0 16px 3px rgba(255, 80, 40, 0.3),
      0 0 28px 6px rgba(255, 50, 30, 0.15);
  }
  50% {
    box-shadow:
      0 0 10px 3px rgba(255, 130, 70, 0.7),
      0 0 24px 6px rgba(255, 90, 50, 0.45),
      0 0 40px 10px rgba(255, 60, 40, 0.2);
  }
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: ${(p) =>
    p.$full
      ? 'linear-gradient(90deg, #e85d3a 0%, #f2884b 40%, #ffb347 75%, #ffe066 100%)'
      : 'linear-gradient(90deg, #5b8fd6 0%, #7baef0 100%)'};
  width: ${(p) => p.$pct}%;
  transition: width 0.3s ease;
  min-width: ${(p) => (p.$pct > 0 ? '2px' : '0')};
`;

const BarCount = styled.span`
  width: 28px;
  flex-shrink: 0;
  font-size: 0.75rem;
  opacity: 0.6;
`;

const DayBarLabel = styled(BarLabel)`
  width: 28px;
`;

const SectionRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin: 28px 0 12px;
`;

const MonthSelect = styled.select`
  padding: 10px 14px;
  border-radius: 8px;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #e9ecf1;
  font-size: 1.1rem;
  min-height: 48px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s ease;
  &:focus { border-color: rgba(91, 143, 214, 0.8); }
  option {
    font-size: 1.1rem;
  }
  @media (max-width: 480px) {
    font-size: 1.15rem;
    min-height: 52px;
  }
`;

/* ── Component ── */

export function CharleyMemoryStatsView({ content, spots, onBack }) {
  const [freqMode, setFreqMode] = useState('week'); // 'month' | 'week'
  const [dowMonth, setDowMonth] = useState(null); // null = all, 'YYYY-MM' = specific month
  const now = new Date();

  const totalMemories = content.length;

  const daysSinceFirstMet = daysBetween(FIRST_MET_DATE, now);
  const daysSinceFirstDate = daysBetween(FIRST_DATE_DATE, now);

  const spotsById = useMemo(() => {
    const map = {};
    if (Array.isArray(spots)) spots.forEach((s) => { if (s && s.id) map[s.id] = s; });
    return map;
  }, [spots]);

  // Unique spots visited
  const uniqueSpotCount = useMemo(() => {
    const ids = new Set();
    content.forEach((entry) => {
      if (Array.isArray(entry.path)) {
        entry.path.forEach((p) => { if (p.spotId) ids.add(p.spotId); });
      }
    });
    return ids.size;
  }, [content]);

  // Top 5 spots
  const topSpots = useMemo(() => {
    const counts = {};
    content.forEach((entry) => {
      if (Array.isArray(entry.path)) {
        entry.path.forEach((p) => {
          if (p.spotId) counts[p.spotId] = (counts[p.spotId] || 0) + 1;
        });
      }
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([id, count]) => ({
        id,
        name: spotsById[id]?.name || id,
        count,
      }));
  }, [content, spotsById]);

  // Frequency chart data (month or week)
  const freqData = useMemo(() => {
    const buckets = {};
    content.forEach((entry) => {
      const ts = entry.timestamp;
      if (!ts) return;
      const dateStr = String(ts).slice(0, 10);
      const key = freqMode === 'month' ? getMonthKey(dateStr) : getWeekKey(dateStr);
      buckets[key] = (buckets[key] || 0) + 1;
    });
    const keys = Object.keys(buckets).sort();
    if (keys.length === 0) return [];
    const result = [];
    if (freqMode === 'month') {
      const [startY, startM] = keys[0].split('-').map(Number);
      const [endY, endM] = keys[keys.length - 1].split('-').map(Number);
      let y = startY, m = startM;
      while (y < endY || (y === endY && m <= endM)) {
        const k = `${y}-${String(m).padStart(2, '0')}`;
        result.push({ label: k, count: buckets[k] || 0, maxDays: daysInMonth(k) });
        m++;
        if (m > 12) { m = 1; y++; }
      }
    } else {
      keys.forEach((k) => result.push({ label: k, count: buckets[k] || 0, maxDays: 7 }));
    }
    return result;
  }, [content, freqMode]);

  // Available months for day-of-week filter
  const availableMonths = useMemo(() => {
    const set = new Set();
    content.forEach((entry) => {
      const ts = entry.timestamp;
      if (!ts) return;
      const mk = String(ts).slice(0, 7);
      if (mk.length === 7) set.add(mk);
    });
    return Array.from(set).sort();
  }, [content]);

  // Day of week distribution (filtered by dowMonth)
  const dayOfWeekData = useMemo(() => {
    const counts = [0, 0, 0, 0, 0, 0, 0];
    content.forEach((entry) => {
      const ts = entry.timestamp;
      if (!ts) return;
      const dateStr = String(ts).slice(0, 10);
      if (dowMonth && dateStr.slice(0, 7) !== dowMonth) return;
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) counts[d.getDay()]++;
    });
    return counts;
  }, [content, dowMonth]);

  const dayMax = Math.max(1, ...dayOfWeekData);

  return (
    <Container>
      <Header>
        <CmBackButton variant="header" onClick={onBack} />
        <Title>統計</Title>
        <span style={{ width: 80 }} />
      </Header>

      <ScrollArea>
        <SectionTitle>總覽</SectionTitle>
        <CardsGrid>
          <NumCard>
            <NumValue>{totalMemories}</NumValue>
            <NumLabel>回憶數</NumLabel>
          </NumCard>
          <NumCard>
            <NumValue>{daysSinceFirstMet}</NumValue>
            <NumLabel>第一次見面至今（天）</NumLabel>
          </NumCard>
          <NumCard>
            <NumValue>{daysSinceFirstDate}</NumValue>
            <NumLabel>第一次約會至今（天）</NumLabel>
          </NumCard>
          <NumCard>
            <NumValue>{uniqueSpotCount}</NumValue>
            <NumLabel>去過的地點</NumLabel>
          </NumCard>
        </CardsGrid>

        {topSpots.length > 0 && (
          <>
            <SectionTitle>最常去的地點</SectionTitle>
            {topSpots.map((spot, i) => (
              <SpotRow key={spot.id}>
                <SpotRank>{i + 1}</SpotRank>
                <SpotName>{spot.name}</SpotName>
                <SpotCount>{spot.count} 次</SpotCount>
              </SpotRow>
            ))}
          </>
        )}

        <SectionTitle>見面頻率</SectionTitle>
        <ToggleRow>
          <ToggleBtn type="button" $active={freqMode === 'month'} onClick={() => setFreqMode('month')}>月</ToggleBtn>
          <ToggleBtn type="button" $active={freqMode === 'week'} onClick={() => setFreqMode('week')}>週</ToggleBtn>
        </ToggleRow>
        {freqData.map((d) => {
          const pct = Math.min((d.count / d.maxDays) * 100, 100);
          const full = d.count >= d.maxDays;
          return (
            <BarRow key={d.label}>
              <BarLabel>{d.label}</BarLabel>
              <BarTrack $full={full}>
                <BarFill $pct={pct} $full={full} />
              </BarTrack>
              <BarCount>{d.count}</BarCount>
            </BarRow>
          );
        })}

        <SectionRow>
          <SectionTitle style={{ margin: 0 }}>星期幾最常見面</SectionTitle>
          <MonthSelect
            value={dowMonth || ''}
            onChange={(e) => setDowMonth(e.target.value || null)}
          >
            <option value="">全部月份</option>
            {availableMonths.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </MonthSelect>
        </SectionRow>
        {DAY_LABELS.map((label, i) => (
          <BarRow key={i}>
            <DayBarLabel>{label}</DayBarLabel>
            <BarTrack>
              <BarFill $pct={(dayOfWeekData[i] / dayMax) * 100} />
            </BarTrack>
            <BarCount>{dayOfWeekData[i]}</BarCount>
          </BarRow>
        ))}
      </ScrollArea>
    </Container>
  );
}
